import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import VideoPlayer from '../components/VideoPlayer';
import SimpleMarkdown from '../components/SimpleMarkdown';
import CommentsSection from '../components/CommentsSection';
import QuizModal, { QuizNoticeModal } from '../components/QuizModal';
import { FiHeart, FiMessageSquare } from 'react-icons/fi';
import './WebinarPublic.css';

const ACCESS_STORAGE_PREFIX = 'webinar_access_';
const QUIZ_PENDING_PREFIX = 'webinar_quiz_pending_';

const readStoredAccessToken = (slug, searchParams) => {
  if (!slug) return '';
  return (searchParams.get('access') || localStorage.getItem(`${ACCESS_STORAGE_PREFIX}${slug}`) || '').trim();
};

const persistAccessToken = (slug, token) => {
  if (!slug || !token) return;
  localStorage.setItem(`${ACCESS_STORAGE_PREFIX}${slug}`, token);
  const url = new URL(window.location.href);
  url.searchParams.set('access', token);
  window.history.replaceState({}, '', url);
};

const quizPendingKey = (slug) => `${QUIZ_PENDING_PREFIX}${slug}`;
const quizNoticeKey = (slug) => `webinar_quiz_notice_${slug}`;

const shouldOfferQuiz = (data) => Boolean(
  data?.quiz_enabled &&
  !data?.quiz_completed &&
  (data?.quiz_questions || []).length &&
  data?.registration_completed
);

const WebinarPublic = () => {
  const { id, slug } = useParams();
  const [searchParams] = useSearchParams();
  const [webinar, setWebinar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [password, setPassword] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [registrationData, setRegistrationData] = useState({});
  const [hologramsData, setHologramsData] = useState({}); // Объект для хранения голограмм по ID полей
  const [isRegistered, setIsRegistered] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [accessExpired, setAccessExpired] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showQuizNotice, setShowQuizNotice] = useState(false);
  const videoPlayerRef = useRef(null);
  const [interfaceSettings, setInterfaceSettings] = useState({
    logo_url: null,
    website_url: '',
    primary_color: '#ff6b35',
    secondary_color: '#ff8c5a',
    accent_color: '#ffa07a',
    timestamp_theory_color: '#d1ecf1',
    timestamp_practice_color: '#fff3cd',
    timestamp_theory_badge_color: '#17a2b8',
    timestamp_practice_badge_color: '#ffc107',
  });

  useEffect(() => {
    const urlPassword = searchParams.get('password');
    if (urlPassword) {
      setPassword(urlPassword);
    }
    fetchWebinar();
    fetchInterfaceSettings();
  }, [id, slug]);

  // Автоматически начинаем с первой временной метки, если она есть
  useEffect(() => {
    if (!webinar || !webinar.timestamps || webinar.timestamps.length === 0) return;
    if (!videoPlayerRef.current) return;
    const firstTimestamp = webinar.timestamps[0];
    if (!firstTimestamp || typeof firstTimestamp.time_seconds !== 'number') return;

    const timeoutId = setTimeout(() => {
      if (!videoPlayerRef.current) return;
      try {
        videoPlayerRef.current.seekTo(firstTimestamp.time_seconds);
      } catch (e) {
        console.error('Error auto-seeking to first timestamp (public):', e);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [webinar]);

  const fetchInterfaceSettings = async () => {
    try {
      const response = await axios.get('/api/settings/interface/');
      setInterfaceSettings(response.data);
    } catch (error) {
      // Fallback на старый endpoint если новый не работает
      try {
        const fallbackResponse = await axios.get('/api/health/interface/');
        setInterfaceSettings(fallbackResponse.data);
      } catch (fallbackError) {
        console.error('Ошибка загрузки настроек интерфейса:', fallbackError);
      }
    }
  };

  useEffect(() => {
    // Применяем цвета через CSS переменные
    if (interfaceSettings.primary_color) {
      document.documentElement.style.setProperty('--primary-color', interfaceSettings.primary_color);
    }
    if (interfaceSettings.secondary_color) {
      document.documentElement.style.setProperty('--secondary-color', interfaceSettings.secondary_color);
    }
    if (interfaceSettings.accent_color) {
      document.documentElement.style.setProperty('--accent-color', interfaceSettings.accent_color);
    }
    if (interfaceSettings.timestamp_theory_color) {
      document.documentElement.style.setProperty('--timestamp-theory-color', interfaceSettings.timestamp_theory_color);
    }
    if (interfaceSettings.timestamp_practice_color) {
      document.documentElement.style.setProperty('--timestamp-practice-color', interfaceSettings.timestamp_practice_color);
    }
    if (interfaceSettings.timestamp_theory_badge_color) {
      document.documentElement.style.setProperty('--timestamp-theory-badge-color', interfaceSettings.timestamp_theory_badge_color);
    }
    if (interfaceSettings.timestamp_practice_badge_color) {
      document.documentElement.style.setProperty('--timestamp-practice-badge-color', interfaceSettings.timestamp_practice_badge_color);
    }
  }, [interfaceSettings]);

  const fetchWebinar = async () => {
    try {
      const storedAccess = readStoredAccessToken(slug, searchParams);
      const params = new URLSearchParams();
      if (password) params.set('password', password);
      if (storedAccess) {
        params.set('access', storedAccess);
        setAccessToken(storedAccess);
      }
      const query = params.toString() ? `?${params.toString()}` : '';

      let url;
      if (slug) {
        url = `/api/webinars/share/${slug}/${query}`;
      } else if (id) {
        url = `/api/webinars/public/${id}/${query}`;
      } else {
        toast.error('Не указан идентификатор вебинара');
        return;
      }
      
      const response = await axios.get(url);
      setWebinar(response.data);
      setAccessExpired(false);
      setIsExpired(false);
      
      // Проверяем истечение времени
      const personalExpires = response.data.registration_access_expires_at;
      const linkExpires = response.data.share_link_expires_at;
      const expireTimes = [personalExpires, linkExpires]
        .filter(Boolean)
        .map((value) => new Date(value).getTime());
      if (expireTimes.length && Math.min(...expireTimes) <= Date.now()) {
        setIsExpired(true);
      }
      
      if (response.data.registration_access_token) {
        persistAccessToken(response.data.share_link_slug || slug, response.data.registration_access_token);
        setAccessToken(response.data.registration_access_token);
      }

      // Проверяем требуется ли регистрация
      if (response.data.share_link_requires_registration && !response.data.registration_completed) {
        const fields = response.data.registration_fields || [];
        const initialHolograms = {};
        fields.forEach(field => {
          if (field.field_type === 'hologram') {
            initialHolograms[field.id] = [''];
          }
        });
        setHologramsData(initialHolograms);
        setShowRegistrationForm(true);
        setIsRegistered(false);
      } else if (response.data.registration_completed) {
        setIsRegistered(true);
        setShowRegistrationForm(false);
      }

      const quizSlug = response.data.share_link_slug || slug;
      if (response.data.quiz_completed && quizSlug) {
        localStorage.removeItem(quizPendingKey(quizSlug));
        sessionStorage.removeItem(quizNoticeKey(quizSlug));
        setShowQuiz(false);
        setShowQuizNotice(false);
      } else if (shouldOfferQuiz(response.data)) {
        if (localStorage.getItem(quizPendingKey(quizSlug)) === '1') {
          setShowQuiz(true);
          setShowQuizNotice(false);
        } else if (sessionStorage.getItem(quizNoticeKey(quizSlug)) !== '1') {
          setShowQuizNotice(true);
        }
      }
      
      checkLike();
    } catch (error) {
      if (error.response?.status === 403) {
        const errorData = error.response.data;
        if (errorData?.access_expired) {
          setAccessExpired(true);
          setIsExpired(true);
          setShowRegistrationForm(false);
        } else if (errorData?.expired) {
          setIsExpired(true);
          setShowPasswordForm(true);
        } else {
          setShowPasswordForm(true);
        }
      } else {
        toast.error('Ошибка загрузки вебинара');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkLike = async () => {
    // Проверка лайка будет реализована через IP
  };

  const handleLike = async () => {
    try {
      if (!webinar || !webinar.id) return;
      
      const response = await axios.post(`/api/webinars/${webinar.id}/like/`);
      setLiked(response.data.liked);
      setWebinar({ 
        ...webinar, 
        likes_count: response.data.likes_count || webinar.likes_count,
        dislikes_count: response.data.dislikes_count || webinar.dislikes_count || 0
      });
    } catch (error) {
      toast.error('Ошибка при добавлении лайка');
    }
  };

  const scrollToComments = () => {
    const el = document.getElementById('comments-section');
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Пытаемся сфокусировать текстовое поле (если оно есть)
    const textarea = el.querySelector('textarea');
    if (textarea) {
      setTimeout(() => textarea.focus(), 300);
    }
  };

  useEffect(() => {
    const expireTimes = [webinar?.registration_access_expires_at, webinar?.share_link_expires_at]
      .filter(Boolean)
      .map((value) => new Date(value).getTime());
    if (!expireTimes.length) {
      setTimeRemaining(null);
      return;
    }
    const expires = Math.min(...expireTimes);

    const updateCountdown = () => {
      const now = new Date().getTime();
      const difference = expires - now;

      if (difference <= 0) {
        setTimeRemaining(null);
        setIsExpired(true);
        return;
      }

      setIsExpired(false);
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeRemaining({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [webinar?.registration_access_expires_at, webinar?.share_link_expires_at]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    fetchWebinar();
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    
    if (!(registrationData.email || '').trim()) {
      toast.error('Укажите email');
      return;
    }

    // Валидация обязательных полей
    const registrationFields = webinar?.registration_fields || [];
    const requiredFields = registrationFields.filter(f => f.is_required);
    
    for (const field of requiredFields) {
      if (field.field_type === 'hologram') {
        const fieldHolograms = hologramsData[field.id] || [];
        const hasHologram = fieldHolograms.some(h => h && h.trim());
        if (!hasHologram) {
          toast.error(`Поле "${field.field_name}" обязательно для заполнения`);
          return;
        }
      } else {
        const fieldKey = field.field_name.toLowerCase().replace(/\s+/g, '_');
        // Маппинг стандартных полей
        let mappedKey = fieldKey;
        const fieldNameLower = field.field_name.toLowerCase();
        if (fieldNameLower.includes('фамилия') || fieldNameLower === 'last_name') {
          mappedKey = 'last_name';
        } else if (fieldNameLower.includes('имя') || fieldNameLower === 'first_name') {
          mappedKey = 'first_name';
        } else if (fieldNameLower.includes('отчество') || fieldNameLower === 'middle_name') {
          mappedKey = 'middle_name';
        } else if (fieldNameLower.includes('компани') || fieldNameLower === 'company') {
          mappedKey = 'company';
        } else if (fieldNameLower.includes('должность') || fieldNameLower === 'position') {
          mappedKey = 'position';
        } else if (fieldNameLower.includes('город') || fieldNameLower === 'city') {
          mappedKey = 'city';
        } else if (fieldNameLower.includes('email') || fieldNameLower.includes('почт') || fieldNameLower === 'e-mail') {
          mappedKey = 'email';
        }
        
        if (!registrationData[mappedKey] || !registrationData[mappedKey].trim()) {
          toast.error(`Поле "${field.field_name}" обязательно для заполнения`);
          return;
        }
      }
    }
    
    try {
      // Собираем все голограммы из всех полей в один массив
      const allHolograms = [];
      registrationFields.forEach(field => {
        if (field.field_type === 'hologram') {
          const fieldHolograms = hologramsData[field.id] || [];
          allHolograms.push(...fieldHolograms.filter(h => h && h.trim()));
        }
      });
      
      const submitData = {
        ...registrationData,
        holograms: allHolograms,
        password: password, // Передаем пароль если был введен
        access: accessToken || readStoredAccessToken(slug, searchParams),
      };
      
      const response = await axios.post(`/api/webinars/share/${slug || webinar?.share_link_slug}/register/`, submitData);
      
      // Если регистрация успешна, обновляем данные вебинара
      if (response.data.registration_completed) {
        setWebinar(response.data);
        
        if (response.data.registration_access_token) {
          persistAccessToken(slug || webinar?.share_link_slug, response.data.registration_access_token);
          setAccessToken(response.data.registration_access_token);
        }
        
        setIsRegistered(true);
        setShowRegistrationForm(false);
        if (response.status === 200) {
          toast.success('Этот email уже регистрировался. Показываем оставшееся время доступа.');
        } else {
          toast.success('Регистрация успешно завершена');
        }
        const quizSlug = response.data.share_link_slug || slug;
        if (shouldOfferQuiz(response.data) && localStorage.getItem(quizPendingKey(quizSlug)) === '1') {
          setShowQuiz(true);
          setShowQuizNotice(false);
        } else if (shouldOfferQuiz(response.data)) {
          setShowQuizNotice(true);
        }
      }
    } catch (error) {
      if (error.response?.status === 403 && (error.response.data?.expired || error.response.data?.access_expired)) {
        setIsExpired(true);
        setAccessExpired(!!error.response.data?.access_expired);
        setShowRegistrationForm(false);
        toast.error(error.response.data?.error || 'Срок доступа истёк');
      } else {
        toast.error(error.response?.data?.error || 'Ошибка регистрации');
      }
    }
  };

  const addHologramField = (fieldId) => {
    const currentHolograms = hologramsData[fieldId] || [''];
    setHologramsData({
      ...hologramsData,
      [fieldId]: [...currentHolograms, '']
    });
  };

  const removeHologramField = (fieldId, index) => {
    const currentHolograms = hologramsData[fieldId] || [''];
    if (currentHolograms.length > 1) {
      setHologramsData({
        ...hologramsData,
        [fieldId]: currentHolograms.filter((_, i) => i !== index)
      });
    }
  };

  const updateHologram = (fieldId, index, value) => {
    const currentHolograms = hologramsData[fieldId] || [''];
    const newHolograms = [...currentHolograms];
    newHolograms[index] = value;
    setHologramsData({
      ...hologramsData,
      [fieldId]: newHolograms
    });
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  if (accessExpired) {
    return (
      <div className="password-form-container">
        <div className="password-form-card">
          <h2>Срок доступа истёк</h2>
          <p>Запись больше недоступна для вашего доступа. Обратитесь к менеджеру, если нужно продление.</p>
        </div>
      </div>
    );
  }

  // Форма регистрации
  if (showRegistrationForm && webinar) {
    const registrationFields = webinar.registration_fields || [];
    
    return (
      <div className="password-form-container">
        <div className="password-form-card registration-form-card">
          <h2>Регистрация на просмотр вебинара</h2>
          <form onSubmit={handleRegistrationSubmit}>
            <div className="form-group">
              <label className="form-label">
                Email <span style={{color: 'red'}}>*</span>
              </label>
              <input
                type="email"
                className="form-input"
                value={registrationData.email || ''}
                onChange={(e) => setRegistrationData({ ...registrationData, email: e.target.value })}
                placeholder="name@example.com"
                required
                autoComplete="email"
              />
            </div>
            {registrationFields.map((field) => {
              if (field.field_type === 'hologram') {
                const fieldHolograms = hologramsData[field.id] || [''];
                return (
                  <div key={field.id} className="form-group">
                    <label className="form-label">
                      {field.field_name} {field.is_required && <span style={{color: 'red'}}>*</span>}
                    </label>
                    {fieldHolograms.map((hologram, index) => (
                      <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                        <input
                          type="text"
                          className="form-input"
                          value={hologram || ''}
                          onChange={(e) => updateHologram(field.id, index, e.target.value)}
                          placeholder={`Голограмма препарата ${index + 1}`}
                          required={field.is_required && index === 0}
                        />
                        {fieldHolograms.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => removeHologramField(field.id, index)}
                            style={{ minWidth: '40px' }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                );
              }
              
              // Стандартные текстовые поля
              let inputType = 'text';
              let placeholder = `Введите ${field.field_name.toLowerCase()}`;
              const fieldKey = field.field_name.toLowerCase().replace(/\s+/g, '_');
              let mappedFieldKey = fieldKey;
              
              const fieldNameLower = field.field_name.toLowerCase();
              if (fieldNameLower.includes('фамилия') || fieldNameLower === 'last_name') {
                mappedFieldKey = 'last_name';
              } else if (fieldNameLower.includes('имя') || fieldNameLower === 'first_name') {
                mappedFieldKey = 'first_name';
              } else if (fieldNameLower.includes('отчество') || fieldNameLower === 'middle_name') {
                mappedFieldKey = 'middle_name';
              } else if (fieldNameLower.includes('компани') || fieldNameLower === 'company') {
                mappedFieldKey = 'company';
              } else if (fieldNameLower.includes('должность') || fieldNameLower === 'position') {
                mappedFieldKey = 'position';
              } else if (fieldNameLower.includes('город') || fieldNameLower === 'city') {
                mappedFieldKey = 'city';
              } else if (fieldNameLower.includes('email') || fieldNameLower.includes('почт') || fieldNameLower === 'e-mail') {
                return null;
              }
              
              return (
                <div key={field.id} className="form-group">
                  <label className="form-label">
                    {field.field_name} {field.is_required && <span style={{color: 'red'}}>*</span>}
                  </label>
                  <input
                    type={inputType}
                    className="form-input"
                    value={registrationData[mappedFieldKey] || ''}
                    onChange={(e) => setRegistrationData({ ...registrationData, [mappedFieldKey]: e.target.value })}
                    placeholder={placeholder}
                    required={field.is_required}
                  />
                </div>
              );
            })}
            
            <div className="form-actions" style={{ marginTop: '20px' }}>
              <button type="submit" className="btn btn-primary btn-block">
                Зарегистрироваться
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (showPasswordForm && !webinar) {
    return (
      <div className="password-form-container">
        <div className="password-form-card">
          {isExpired ? (
            <>
              <h2>Срок действия ссылки истек</h2>
              <p>Вебинар больше недоступен. Обратитесь к вашему менеджеру для получения новой ссылки.</p>
            </>
          ) : (
            <>
              <h2>Требуется пароль</h2>
              <p>Для просмотра этого вебинара требуется пароль</p>
              <form onSubmit={handlePasswordSubmit}>
                <input
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Введите пароль"
                  required
                  disabled={isExpired}
                />
                <button type="submit" className="btn btn-primary btn-block" disabled={isExpired}>
                  Войти
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!webinar) {
    return <div className="empty-state">Вебинар не найден</div>;
  }

  if (isExpired) {
    return (
      <div className="password-form-container">
        <div className="password-form-card">
          <h2>{accessExpired || webinar.registration_access_expires_at ? 'Срок доступа истёк' : 'Срок действия ссылки истек'}</h2>
          <p>Вебинар больше недоступен. Обратитесь к вашему менеджеру для получения новой ссылки.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="webinar-public">
      {/* Header с логотипом */}
      {interfaceSettings.logo_url && (
        <header className="public-header">
          <div className="public-header-content">
            {interfaceSettings.website_url ? (
              <a 
                href={interfaceSettings.website_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="public-logo-link"
              >
                <img src={interfaceSettings.logo_url} alt="Логотип" className="public-logo" />
              </a>
            ) : (
              <img src={interfaceSettings.logo_url} alt="Логотип" className="public-logo" />
            )}
          </div>
        </header>
      )}
      
      {timeRemaining && (
        <div className={`access-timer ${timeRemaining.days < 1 ? 'is-urgent' : ''}`}>
          <div className="access-timer-copy">
            <span className="access-timer-kicker">Доступность записи</span>
            <strong>Осталось времени на просмотр</strong>
          </div>
          <div className="access-timer-units" aria-live="polite">
            <div className="access-timer-unit">
              <b>{timeRemaining.days}</b>
              <span>дн</span>
            </div>
            <div className="access-timer-unit">
              <b>{String(timeRemaining.hours).padStart(2, '0')}</b>
              <span>час</span>
            </div>
            <div className="access-timer-unit">
              <b>{String(timeRemaining.minutes).padStart(2, '0')}</b>
              <span>мин</span>
            </div>
            <div className="access-timer-unit">
              <b>{String(timeRemaining.seconds).padStart(2, '0')}</b>
              <span>сек</span>
            </div>
          </div>
        </div>
      )}

      {/* Видео */}
      <div className="public-video-container">
        <VideoPlayer 
          ref={videoPlayerRef} 
          url={(function () {
            if (webinar.external_peertube_url) {
              return (webinar.external_peertube_url || '').trim() || null;
            }
            if (webinar.external_video_url) {
              let raw = (webinar.external_video_url || '').trim();
              if (!raw) return null;
              try {
                const u = new URL(raw);
                if (!u.pathname.endsWith('/download')) {
                  if (!u.pathname.endsWith('/')) {
                    u.pathname += '/';
                  }
                  u.pathname += 'download';
                }
                return u.toString();
              } catch (e) {
                if (!raw.endsWith('/download')) {
                  raw = raw.replace(/\/+$/, '') + '/download';
                }
                return raw;
              }
            }
            return webinar.video_file;
          })()}
          // Для внешних ссылок (Nextcloud) токены не добавляем
          shareSlug={webinar.external_video_url ? undefined : slug}
          password={webinar.external_video_url ? undefined : password}
          accessToken={webinar.external_video_url ? undefined : accessToken}
          webinarTitle={webinar?.title}
          webinarDate={webinar?.recording_date}
          speakerName={webinar?.speaker_data?.full_name}
          chronologyStructure={webinar?.chronology_structure}
          onEnded={() => {
            if (!shouldOfferQuiz(webinar)) return;
            const quizSlug = webinar.share_link_slug || slug;
            if (quizSlug) {
              localStorage.setItem(quizPendingKey(quizSlug), '1');
            }
            setShowQuizNotice(false);
            setShowQuiz(true);
          }}
        />
      </div>

      {shouldOfferQuiz(webinar) && !showQuiz && (
        <div className="quiz-end-fallback">
          <p>После просмотра записи откроется опрос. Если вы уже досмотрели до конца — откройте его здесь.</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              const quizSlug = webinar.share_link_slug || slug;
              if (quizSlug) {
                localStorage.setItem(quizPendingKey(quizSlug), '1');
              }
              setShowQuizNotice(false);
              setShowQuiz(true);
            }}
          >
            Открыть опрос
          </button>
        </div>
      )}

      {webinar.chronology_structure && webinar.chronology_structure.trim() ? (
        <details className="timestamps-above-title">
          <summary>Хронология и структура</summary>
          <div className="chronology-lines">
            {webinar.chronology_structure.split('\n').map((line, idx) => {
              const text = (line || '').trim();
              if (!text) return null;
              const m = text.match(/\b(\d{2}:\d{2}:\d{2})\b/);
              const displayText = m ? text.replace(m[1], '').replace(/\s{2,}/g, ' ').trim() : text;
              const sec = m
                ? m[1].split(':').map((v) => parseInt(v, 10)).reduce((acc, v) => acc * 60 + v, 0)
                : null;
              return (
                m && typeof sec === 'number' ? (
                  <button
                    key={`${text}-${idx}`}
                    type="button"
                    className="chronology-line-item chronology-line-item-clickable"
                    onClick={() => videoPlayerRef.current?.seekTo(sec)}
                    title="Перейти к фрагменту"
                  >
                    <span className="summary-time-link">{m[1]}</span>
                    <SimpleMarkdown text={displayText} />
                  </button>
                ) : (
                  <div key={`${text}-${idx}`} className="chronology-line-item">
                    <SimpleMarkdown text={displayText} />
                  </div>
                )
              );
            })}
          </div>
        </details>
      ) : (
        webinar.timestamps && webinar.timestamps.length > 0 && (
          <div className="timestamps-above-title">
            <div className="timestamps-list-compact">
              {webinar.timestamps.map((ts) => (
                <button
                  key={ts.id}
                  className={`timestamp-btn-compact timestamp-${ts.type}`}
                  onClick={() => {
                    if (videoPlayerRef.current) {
                      try {
                        videoPlayerRef.current.seekTo(ts.time_seconds);
                      } catch (error) {
                        console.error('Error seeking to timestamp:', error);
                      }
                    }
                  }}
                >
                  <span className="timestamp-time-compact">{ts.formatted_time}</span>
                  <span className="timestamp-label-compact">{ts.label}</span>
                  <span className={`timestamp-badge-compact badge-${ts.type}`}>
                    {ts.type === 'practice' ? 'Практика' : 'Теория'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )
      )}

      {/* Кнопки лайк/комментарии (под видео) */}
      <div className="public-actions-under-video">
        <div className="public-like-dislike-buttons">
          <button
            className={`like-btn ${liked ? 'liked' : ''}`}
            onClick={handleLike}
            title="Лайк"
          >
            <FiHeart /> {webinar.likes_count || 0}
          </button>

          {webinar.comments_enabled && (
            <button
              type="button"
              className="comment-btn"
              onClick={scrollToComments}
              title="Комментарии"
            >
              <FiMessageSquare /> {webinar.comments_count || 0}
            </button>
          )}
        </div>
      </div>

      {/* Спикер */}
      {webinar.speaker_data && (
        <div className="speaker-info-compact">
          <div className="speaker-photo-container-small">
            {webinar.speaker_data.photo ? (
              <img 
                src={webinar.speaker_data.photo.startsWith('http') 
                  ? webinar.speaker_data.photo 
                  : `${process.env.REACT_APP_API_URL || 'http://172.16.46.199'}${webinar.speaker_data.photo}`} 
                alt={webinar.speaker_data.full_name}
                className="speaker-photo-small"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const placeholder = e.target.parentElement.querySelector('.speaker-photo-placeholder-small');
                  if (placeholder) {
                    placeholder.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div 
              className="speaker-photo-placeholder-small" 
              style={{ display: webinar.speaker_data.photo ? 'none' : 'flex' }}
            >
              {webinar.speaker_data.full_name?.charAt(0) || '?'}
            </div>
          </div>
          <div className="speaker-details-compact">
            <h3 className="speaker-name-compact">{webinar.speaker_data.full_name}</h3>
            {webinar.speaker_data.credentials && (
              <p className="speaker-credentials-compact">{webinar.speaker_data.credentials}</p>
            )}
          </div>
        </div>
      )}

      {/* Комментарии */}
      {webinar.comments_enabled && (
        <div className="public-comments">
          <CommentsSection webinarId={webinar.id} />
        </div>
      )}

      <QuizNoticeModal
        open={showQuizNotice && shouldOfferQuiz(webinar) && !showQuiz}
        onContinue={() => {
          const quizSlug = webinar.share_link_slug || slug;
          if (quizSlug) {
            sessionStorage.setItem(quizNoticeKey(quizSlug), '1');
          }
          setShowQuizNotice(false);
        }}
      />

      <QuizModal
        open={showQuiz && shouldOfferQuiz(webinar)}
        slug={webinar.share_link_slug || slug}
        accessToken={accessToken || readStoredAccessToken(webinar.share_link_slug || slug, searchParams)}
        questions={webinar.quiz_questions || []}
        onSubmitted={() => {
          const quizSlug = webinar.share_link_slug || slug;
          if (quizSlug) {
            localStorage.removeItem(quizPendingKey(quizSlug));
            sessionStorage.removeItem(quizNoticeKey(quizSlug));
          }
          setShowQuiz(false);
          setShowQuizNotice(false);
          setWebinar((prev) => (prev ? { ...prev, quiz_completed: true } : prev));
        }}
      />
    </div>
  );
};

export default WebinarPublic;
